import { useCallback, useMemo } from "react"
import { Box, ToggleButtonGroup, useTheme } from "@mui/material"
import { PercentIcon } from "components/icons"

import {
  CURRENT_PROMOTION_FILTER_VALUE,
  UPCOMING_PROMOTION_FILTER_VALUE,
} from "constant"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { usePromotion } from "hooks/promotion/usePromotion"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { VenueSelector } from "components/VenueSelector"
import { EmptyData } from "components/EmptyData"

import {
  FeatureCarouselLayout,
  FeatureGridLayout,
} from "components/domains/features"

import { CategoryFilterChip } from "components/CategoryFilterBar"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { IContentWithAsideProps } from "layouts/kiosk-h/ContentWithAside"
import clsx from "clsx"
import { useNavigate } from "react-router-dom"
import { Id } from "@turf/helpers"
import {
  IVerticalPromotionCardProps,
  VerticalPromotionCard,
} from "components/domains/promotions"
import { PromotionData } from "providers/venue/types"

interface IPromotionsKioskHPageProps {
  Layout?: React.FC<IContentWithAsideProps>
  AppNavigation?: React.ComponentType
}

export const PromotionsKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}: IPromotionsKioskHPageProps) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const {
    isMultiVenueProject,
    venueFilter,
    venues,
    locationVenueId,
    groupPromotions,
    activeGroupPromotion,
    promotions,
    handleChange,
    changeVenueFilter,
    layout: promotionLayoutType,
    displayMode: promotionDisplayMode,
  } = usePromotion()

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const handleClickCard = useCallback(
    (id: Id) => {
      navigate(`/promotions/${id}`)
    },
    [navigate]
  )

  const theme = useTheme()

  const { promotionCarouselOption } =
    theme.components[`PromotionPage`]?.defaultProps || {}

  const { styleOverrides } = theme.components?.[`PromotionPage`] || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    emptyDataWrapper,
    venueSelector,
    promotionGroupSelectorButton,
    gridContainer,
  } = styleOverrides

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const displayPromotions = useMemo(() => {
    return promotionDisplayMode === "all"
      ? promotions
      : groupPromotions[activeGroupPromotion]
  }, [promotionDisplayMode, promotions, groupPromotions, activeGroupPromotion])

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent
          sx={pageCardContent}
          className={clsx(
            `PromotionLayoutType-${promotionLayoutType}`,
            `PromotionDisplayMode-${promotionDisplayMode}`
          )}
        >
          {displayPromotions && displayPromotions.length > 0 ? (
            promotionLayoutType === "carousel" ? (
              <FeatureCarouselLayout<IVerticalPromotionCardProps, PromotionData>
                sx={carouselContainer}
                features={displayPromotions}
                carouselOption={promotionCarouselOption}
                carouselSx={carousel}
                onClickCard={handleClickCard}
                cardComponent={VerticalPromotionCard}
              />
            ) : (
              <FeatureGridLayout<IVerticalPromotionCardProps>
                sx={gridContainer}
                features={displayPromotions}
                onClickCard={handleClickCard}
                cardComponent={VerticalPromotionCard}
              />
            )
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t("No Current Promotions")}
                secondary={t("Check Back Soon")}
              />
            </Box>
          )}

          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}

          {promotionDisplayMode === "grouped" && (
            <Box sx={promotionGroupSelectorButton}>
              <ToggleButtonGroup
                value={activeGroupPromotion}
                onChange={handleChange}
                exclusive
              >
                <CategoryFilterChip value={CURRENT_PROMOTION_FILTER_VALUE}>
                  {t("Current Events")}
                </CategoryFilterChip>
                <CategoryFilterChip value={UPCOMING_PROMOTION_FILTER_VALUE}>
                  {t("Upcoming Events")}
                </CategoryFilterChip>
              </ToggleButtonGroup>
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
